import {
  FALLBACK_LANGUAGE,
  SUPPORTED_LANGUAGES,
  TRANSLATED_LANGUAGES,
  UNKNOWN_BRAND_LANGUAGES,
} from './LocalizationManager';

/**
 * Languages a brand's users may switch between, primary first, English always present
 * as the fallback. A brand without a known set (legacy cache, unreachable
 * brand-config) offers the translated bundles.
 */
export function brandLanguages(brand) {
  const languages = (brand?.languages ?? []).filter((language) => SUPPORTED_LANGUAGES.includes(language));

  if (!languages.length) {
    return UNKNOWN_BRAND_LANGUAGES;
  }

  return languages.includes(FALLBACK_LANGUAGE) ? languages : [...languages, FALLBACK_LANGUAGE];
}

/**
 * The language a brand's screens open in: its primary language once that has a
 * translation bundle, until then the first translated one — in practice English.
 */
export function defaultBrandLanguage(brand) {
  return brandLanguages(brand).find((language) => TRANSLATED_LANGUAGES.includes(language)) ?? FALLBACK_LANGUAGE;
}

/**
 * Languages an operator surface may offer for a given outlet: what the brand offers,
 * plus the outlet's own configured language, that one first.
 *
 * The outlet language has to be in the set even when the brand does not list it. The My
 * Store picker offers every language the platform ships, and per the ticket's settings
 * map that value is "the language customers see on the storefront and that operator
 * surfaces default to for this outlet". Gate it away and the same app that let you
 * choose Polish quietly discards it — the storefront turns Polish and the dashboard
 * stays German. Brand alignment still governs everything else, so a German session
 * cannot leak onto a Spanish partner's screens.
 *
 * `outletLanguage` is companies.language, the raw column: NULL while the outlet inherits
 * from its group or partner, which is exactly when the brand default is the right answer
 * anyway.
 */
export function operatorLanguages(brand, outletLanguage) {
  const offered = brandLanguages(brand);

  if (!outletLanguage || !SUPPORTED_LANGUAGES.includes(outletLanguage) || offered.includes(outletLanguage)) {
    return offered;
  }

  return [outletLanguage, ...offered];
}

/**
 * Keeps the current language when the brand offers it (a partner's language pick, the
 * browser language, `?lng=`), otherwise the brand default — a German session must not
 * leak onto a Spanish partner's screens.
 */
export function resolveBrandLanguage(brand, currentLanguage) {
  if (currentLanguage && brandLanguages(brand).includes(currentLanguage)) {
    return currentLanguage;
  }

  return defaultBrandLanguage(brand);
}
